import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Header, LwButton, Page, Toolbox, useFormButtons } from 'redesign';
import { CompanyEmploymentTypes } from '@types';
import { useAlert } from 'shared/components/alerts';
import { useExternalParams } from 'shared/hooks';
import { useWorkTemplateCreateDefaults } from './hooks/use-work-template-create-defaults';
import { useCompanyRelativePath } from '../../../../../../hooks';
import { useCreateWorkTemplate } from '../../../../../../hooks/queryHooks/jobs/use-create-work-template';
import { usePageProvider } from '../../../../../../layouts';
import { routes } from '../../../../../../Routes';
import { WorkTemplateCreateDataModel } from '../../../../../../services/mappers/mappers';
import { WorkTemplateForm } from '../../forms/shared/work-template-form';
import { decodeWorkTemplateFormToWorkTemplate } from '../utils';
import { WorkTemplateFormContext } from '../../forms/shared/work-template-form.types';
import { WORK_TEMPLATE_INITIAL_VALUES } from '../../work-templates.types';

type Props = {
  workTemplateCreateData: WorkTemplateCreateDataModel;
  defaultValues?: Partial<WorkTemplateFormContext>;
  successMessage: string;
  pageTitle: string;
  employmentType: CompanyEmploymentTypes;
};

export const WorkTemplateCreatePage = ({
  workTemplateCreateData,
  defaultValues,
  pageTitle,
  successMessage,
  employmentType,
}: Props) => {
  const formDefaultValues: WorkTemplateFormContext = useWorkTemplateCreateDefaults(
    defaultValues,
    employmentType
  );
  const formMethods = useForm<WorkTemplateFormContext>({
    defaultValues: formDefaultValues,
  });

  const { alertSuccess, alertError } = useAlert();
  const generatePath = useCompanyRelativePath();
  const [{ filters }] = usePageProvider();
  const [params] = useExternalParams(WORK_TEMPLATE_INITIAL_VALUES);
  const queryParams = {
    ...filters,
    search: params.search || undefined,
  };

  const navigate = useNavigate();
  const createWorkTemplate = useCreateWorkTemplate();
  const { submitButtonProps, cancelButtonProps, returnTo } = useFormButtons(
    createWorkTemplate.status
  );

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      const decodedData = decodeWorkTemplateFormToWorkTemplate(data);

      createWorkTemplate.mutate(decodedData, {
        onSuccess: ({ id }) => {
          alertSuccess(successMessage);
          navigate(generatePath(routes.WorkTemplatesDetail, { id }, queryParams));
        },
        onError: (e) => alertError(e),
      });
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={pageTitle} />}
        toolbox={
          <Toolbox underline>
            <LwButton onClick={handleSubmit} iconColorMode="stroke" {...submitButtonProps}>
              Maak aan
            </LwButton>
            <LwButton
              onClick={returnTo(routes.WorkTemplates, undefined, queryParams)}
              iconColorMode="fill"
              {...cancelButtonProps}
            >
              Annuleer
            </LwButton>
          </Toolbox>
        }
      >
        <WorkTemplateForm
          formState="create"
          employmentType={employmentType}
          workTemplateCreateData={workTemplateCreateData}
        />
      </Page>
    </FormProvider>
  );
};
